/**
 * usePreferences — ViewModel for /dashboard/preferences/settings.
 *
 * Single GET fetches every editable row in one response. PATCH per
 * row mutates a single value.
 *
 * Server is the source of truth for `value`; this hook is the source
 * of truth for "what's currently rendered" + "did the save succeed".
 *
 * Renamed 2026-05-19 from useSystemConfig alongside the System →
 * Preferences rebrand + move of the API surface to its own
 * preferences worker.
 *
 * PAGINATION EXEMPTION (SOP 02 §"List Pagination" / SOP 15 Ch.21
 * — Kiran 2026-05-17):
 *   This is NOT a paginated list. It's a fixed-keyset configuration
 *   form whose row set is bounded by the PREFERENCESREGISTRY
 *   constant in code — the catalogue size only grows with releases,
 *   never with runtime data. The Preferences screen renders the rows
 *   in section tabs so the operator finds a specific key by name,
 *   not by scrolling/paging.
 *   Forcing pagination here would hurt UX (operator looking for
 *   PAGINATION.PAGESIZEDEFAULT shouldn't have to page through to
 *   find it) and would not satisfy any growth scenario. The carve-
 *   out is documented in SOP 02 — see "Fixed-keyset config forms".
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { PreferenceConfigDto } from '../models/preferences-config.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

// Single-source-of-truth: src/models/preferences-config.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { PreferenceConfigDto } from '../models/preferences-config.model'

interface ListBody {
  readonly success: true
  readonly data: { readonly items: PreferenceConfigDto[] }
}

export interface UsePreferencesReturn {
  readonly items: PreferenceConfigDto[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly update: (
    key: string,
    value: number | boolean | string
  ) => Promise<
    { ok: true; data: PreferenceConfigDto }
    | { ok: false; field: string | null; message: string }
  >
}

export function usePreferences(): UsePreferencesReturn {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<PreferenceConfigDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState<number>(0)

  useEffect(() => {
    const ctrl = new AbortController()
    let cancelled = false
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await authedFetch(APIROUTES.PREFERENCESCONFIG, { signal: ctrl.signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          if (!cancelled) {
            setError(message)
            setIsLoading(false)
          }
          return
        }
        const body = (await res.json()) as ListBody
        if (!cancelled) {
          setItems(body.data.items)
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : String(err))
          setIsLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [authedFetch, reloadTick])

  const reload = useCallback(() => setReloadTick((n) => n + 1), [])

  const update = useCallback(
    async (key: string, value: number | boolean | string) => {
      const r = await mutateJson<PreferenceConfigDto>(
        authedFetch,
        fillPath(APIROUTES.PREFERENCESCONFIGITEM, { key }),
        'PATCH',
        { value }
      )
      if (!r.ok) return r
      setItems((prev) => prev.map((row) => (row.key === key ? r.data : row)))
      return { ok: true as const, data: r.data }
    },
    [authedFetch]
  )

  return { items, isLoading, error, reload, update }
}

/**
 * Convenience filter — pluck the rows in one section. The order
 * server-side already follows the registry order, so this preserves it.
 */
export function filterBySection(
  items: ReadonlyArray<PreferenceConfigDto>,
  section: PreferenceConfigDto['section']
): PreferenceConfigDto[] {
  return items.filter((row) => row.section === section)
}
